import React from 'react';
import { useTranslation } from 'react-i18next';

// Aviso de Privacidad Integral — Confidenza Seguros & Fianzas.
// Full legal text, bilingual (ES source + EN translation). Rendered from
// structured data so both languages share one layout. Linked from the footer
// (Header/Footer "Aviso de Privacidad" / "Privacy Policy").

type Block =
  | { type: 'p'; lead?: string; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'table'; headers: [string, string]; rows: Array<[string, string]> };

interface Section {
  n?: string;
  title: string;
  blocks: Block[];
}

interface PrivacyDoc {
  title: string;
  version: string;
  intro: string;
  sections: Section[];
}

const es: PrivacyDoc = {
  title: 'Aviso de Privacidad Integral',
  version: 'Confidenza Seguros & Fianzas  |  Versión vigente: 11 de agosto de 2026',
  intro:
    'Este aviso explica qué datos personales tratamos, para qué los utilizamos, con quién los compartimos y cómo puede ejercer sus derechos.',
  sections: [
    {
      n: '1',
      title: 'Identidad y domicilio del responsable',
      blocks: [
        {
          type: 'p',
          text: 'Maria de Lourdes Camez Ramos y Rodolfo Olea Patiño, quienes operan conjuntamente bajo el nombre comercial Confidenza Seguros & Fianzas (en adelante, “Confidenza”), son responsables del tratamiento de sus datos personales. Su domicilio común es Avenida Lázaro Cárdenas 2501, local POS 10-A, interior Plaza Pioneros, colonia El Médano, Cabo San Lucas, Baja California Sur, C.P. 23453, México.',
        },
        {
          type: 'p',
          text: 'Cada agente será responsable de los expedientes, pólizas, fianzas y operaciones intermediados bajo su respectiva clave de agente. Ambos serán responsables del tratamiento efectuado mediante los correos, archivos, bases de datos, sistemas, proveedores tecnológicos y procesos administrativos compartidos del despacho. Las solicitudes relacionadas con cualquiera de los dos responsables serán atendidas por el mismo canal de privacidad.',
        },
        {
          type: 'p',
          text: 'Contacto de privacidad: administracion@confidenzaseguros.com | Tel. 624 131 6666 | www.confidenzaseguros.com',
        },
      ],
    },
    {
      n: '2',
      title: 'Personas a quienes aplica este aviso',
      blocks: [
        {
          type: 'p',
          text: 'Aplica a solicitantes, prospectos, contratantes, asegurados, afianzados, beneficiarios, proveedores de recursos, obligados solidarios, fiados, terceros afectados, reclamantes, visitantes del sitio web, contactos comerciales, proveedores, aspirantes, colaboradores y cualquier persona cuyos datos sean tratados por Confidenza.',
        },
      ],
    },
    {
      n: '3',
      title: 'Datos personales que podemos tratar',
      blocks: [
        {
          type: 'p',
          text: 'Dependiendo de la relación y del producto o servicio solicitado, podremos tratar las categorías siguientes:',
        },
        {
          type: 'ul',
          items: [
            'Identificación y contacto: nombre, razón social, fecha de nacimiento, edad, sexo, nacionalidad, estado civil, domicilio, teléfonos, correo electrónico, firma, fotografía e identificaciones oficiales.',
            'Fiscales y de facturación: RFC, constancia de situación fiscal, régimen fiscal y datos necesarios para emitir comprobantes.',
            'Laborales y académicos: ocupación, puesto, empleador, antigüedad, ingresos, experiencia, referencias y estudios.',
            'Patrimoniales y financieros: ingresos, bienes, cuentas bancarias, CLABE, datos de pago, historial y capacidad financiera, y demás información necesaria para cotizar, contratar o administrar seguros y fianzas. Confidenza no debe conservar códigos de seguridad de tarjetas.',
            'Jurídicos, administrativos, de seguros y fianzas: información contractual, pólizas, reclamaciones, siniestros, garantías, expedientes, procedimientos administrativos o jurisdiccionales, tránsito y migración, cuando corresponda.',
            'Datos de terceros: beneficiarios, dependientes, familiares, referencias, conductores, propietarios, fiados, obligados solidarios o terceros afectados. Quien proporcione esos datos deberá contar con facultades o autorización para hacerlo y comunicarles este aviso.',
            'Datos técnicos y de navegación: dirección IP, identificadores de dispositivo, navegador, sistema operativo, páginas consultadas, fecha y hora, origen de la visita, cookies y tecnologías similares.',
          ],
        },
        {
          type: 'p',
          lead: 'Datos personales sensibles.',
          text: 'Cuando sea indispensable para evaluar riesgos, cotizar, contratar o gestionar reclamaciones, podremos tratar datos de salud, antecedentes médicos, discapacidad, características físicas, datos biométricos u otros datos sensibles pertinentes. No solicitaremos información sobre afiliación sindical o vida sexual salvo que sea estrictamente necesaria y jurídicamente procedente para el caso concreto.',
        },
        {
          type: 'p',
          text: 'Para datos patrimoniales o financieros y, especialmente, para datos sensibles, recabaremos el consentimiento expreso o expreso y por escrito cuando la legislación lo exija, salvo las excepciones legales aplicables.',
        },
      ],
    },
    {
      n: '4',
      title: 'Finalidades primarias y necesarias',
      blocks: [
        { type: 'p', text: 'Trataremos sus datos para las finalidades indispensables siguientes:' },
        {
          type: 'ul',
          items: [
            'Identificarle, contactar con usted, integrar y actualizar su expediente y verificar la información proporcionada.',
            'Conocer sus necesidades, evaluar riesgos, solicitar y comparar cotizaciones, elaborar propuestas y gestionar solicitudes de seguros o fianzas.',
            'Intermediar, contratar, emitir, administrar, mantener, modificar, cobrar, financiar, renovar o cancelar pólizas, fianzas, endosos y servicios relacionados.',
            'Gestionar reclamaciones, siniestros, asistencias, indemnizaciones, salvamentos, recuperación de daños y atención con aseguradoras, afianzadoras y prestadores de servicios.',
            'Realizar validaciones de identidad, solvencia, prevención de fraude, prevención de operaciones ilícitas y cumplimiento regulatorio.',
            'Cumplir obligaciones contractuales, fiscales, contables, de seguros y fianzas, así como atender requerimientos de autoridades competentes.',
            'Atender solicitudes, quejas, aclaraciones, consultas, derechos ARCO, revocaciones y limitaciones de uso o divulgación.',
            'Proteger la seguridad de las personas, la información, los sistemas y las instalaciones; generar respaldos, bitácoras y evidencia de operaciones.',
            'Seleccionar, contratar y administrar personal; verificar referencias; cumplir obligaciones laborales y de seguridad social, cuando la persona sea aspirante o colaboradora.',
            'Administrar relaciones con proveedores y prestadores de servicios, incluidos pagos, controles de acceso y cumplimiento contractual.',
            'Elaborar estadísticas, modelos de servicio y análisis internos usando, cuando sea viable, datos agregados o disociados.',
          ],
        },
      ],
    },
    {
      n: '5',
      title: 'Finalidades secundarias',
      blocks: [
        {
          type: 'p',
          text: 'Cuando sean aplicables, podremos utilizar datos de contacto y perfil comercial para enviar información sobre productos, renovaciones, promociones, encuestas, eventos y contenidos de Confidenza, así como para medir la calidad del servicio. Estas finalidades no son necesarias para mantener la relación jurídica.',
        },
        {
          type: 'p',
          text: 'Puede negarse o retirar su autorización para estas finalidades en cualquier momento escribiendo a administracion@confidenzaseguros.com con el asunto “Negativa de mercadotecnia”. La negativa no afectará los productos o servicios contratados.',
        },
      ],
    },
    {
      n: '6',
      title: 'Proveedores tecnológicos, automatización e inteligencia artificial',
      blocks: [
        {
          type: 'p',
          text: 'Confidenza puede apoyarse en personas encargadas que presten servicios de alojamiento, nube, correo, comunicaciones, ciberseguridad, respaldo, gestión documental, CRM, análisis, soporte y automatización asistida por inteligencia artificial, incluyendo plataformas como Handle, cuando se encuentren habilitadas por Confidenza.',
        },
        {
          type: 'p',
          text: 'Estos proveedores tratarán datos únicamente por cuenta e instrucciones de Confidenza, para las finalidades descritas en este aviso y conforme a contratos y medidas de confidencialidad y seguridad. Podrán operar infraestructura dentro o fuera de México. Confidenza procurará aplicar controles de acceso, minimización y configuración que impidan usos incompatibles.',
        },
        {
          type: 'p',
          text: 'Principales categorías de proveedores que pueden tratar datos por cuenta de Confidenza:',
        },
        {
          type: 'table',
          headers: ['Proveedor o categoría', 'Servicio realizado por cuenta de Confidenza'],
          rows: [
            ['Handle', 'Automatización e inteligencia artificial aplicada a operaciones de seguros, análisis y apoyo en tareas administrativas.'],
            ['Quattro CRM / Copsis', 'Administración de clientes, pólizas, renovaciones, cobranza, documentación y seguimiento de siniestros.'],
            ['Correo, nube y almacenamiento', 'Recepción de comunicaciones, alojamiento, respaldo, intercambio y conservación de archivos y expedientes.'],
            ['Comunicaciones, soporte y ciberseguridad', 'Atención por canales digitales, mantenimiento, monitoreo, control de acceso y protección de sistemas.'],
            ['Otros CRM o plataformas equivalentes', 'Sustituir o complementar las funciones anteriores bajo obligaciones equivalentes de privacidad y seguridad.'],
          ],
        },
        {
          type: 'p',
          text: 'Los proveedores identificados son enunciativos y pueden ser sustituidos o complementados por otros que presten funciones equivalentes, siempre que actúen por cuenta e instrucciones de Confidenza y asuman obligaciones contractuales equivalentes de privacidad, confidencialidad y seguridad.',
        },
        {
          type: 'p',
          text: 'Salvo que se informe y autorice expresamente de otra manera, Confidenza no permitirá que los proveedores utilicen datos personales o expedientes de clientes para entrenar modelos de propósito general propios o de terceros. El personal de Confidenza revisará la información relevante antes de adoptar decisiones que produzcan efectos jurídicos o afecten de manera significativa a una persona; la IA se utilizará como apoyo y no sustituirá la valoración humana ni la decisión que corresponda a aseguradoras, afianzadoras o autoridades.',
        },
        {
          type: 'p',
          text: 'Puede solicitar información sobre el tratamiento asistido por IA, pedir revisión humana o manifestar una objeción mediante el canal de privacidad indicado en este aviso. La viabilidad de la solicitud dependerá de las obligaciones contractuales y legales aplicables.',
        },
      ],
    },
    {
      n: '7',
      title: 'Remisiones y transferencias',
      blocks: [
        { type: 'p', lead: '7.1 Personas encargadas (remisiones).', text: 'Confidenza podrá remitir datos a proveedores que actúen por su cuenta —incluidos servicios tecnológicos y de IA— sin que ello constituya una transferencia, siempre que traten la información conforme a las instrucciones y obligaciones contractuales de Confidenza.' },
        { type: 'p', lead: '7.2 Transferencias que pueden realizarse.', text: 'Podremos transferir los datos necesarios a los receptores siguientes:' },
        {
          type: 'table',
          headers: ['Receptor o categoría', 'Finalidad'],
          rows: [
            ['Aseguradoras, afianzadoras, reaseguradoras y reafianzadoras', 'Cotizar, seleccionar riesgos, emitir y administrar contratos, atender reclamaciones y prevenir fraude.'],
            ['Agentes, ajustadores, peritos, hospitales, médicos, talleres, asistencias, abogados y demás prestadores vinculados al servicio', 'Prestar asistencias, valorar daños o salud, tramitar siniestros, reclamaciones y defensas.'],
            ['Autoridades administrativas, fiscales, financieras, judiciales y de seguridad social', 'Cumplir obligaciones legales y atender requerimientos fundados.'],
            ['Instituciones y organizaciones del sector asegurador y afianzador', 'Selección de riesgos, intercambio permitido por ley, prevención de fraude y cumplimiento.'],
            ['Bancos, procesadores de pago y entidades financieras', 'Procesar cobros, devoluciones, pagos e indemnizaciones.'],
            ['Sociedades relacionadas con Confidenza', 'Administración interna y continuidad operativa; para mercadotecnia, sólo cuando exista base jurídica o consentimiento aplicable.'],
          ],
        },
        {
          type: 'p',
          text: 'Cuando una transferencia requiera consentimiento, Confidenza lo solicitará mediante el mecanismo correspondiente. No se requerirá consentimiento en los casos previstos por la legislación aplicable, entre otros, cuando la transferencia sea necesaria para mantener o cumplir una relación jurídica, cumplir una ley o atender a una autoridad competente.',
        },
      ],
    },
    {
      n: '8',
      title: 'Derechos ARCO y otros controles',
      blocks: [
        {
          type: 'p',
          text: 'Usted puede solicitar el acceso a sus datos; su rectificación; la cancelación cuando proceda; u oponerse a tratamientos específicos. También puede revocar su consentimiento y limitar el uso o divulgación de sus datos.',
        },
        {
          type: 'p',
          text: 'Envíe su solicitud a administracion@confidenzaseguros.com o entréguela en el domicilio indicado. Deberá incluir:',
        },
        {
          type: 'ol',
          items: [
            'Nombre y medio para comunicar la respuesta.',
            'Documento que acredite su identidad y, en su caso, la representación legal.',
            'Descripción clara de los datos y del derecho que desea ejercer.',
            'Para rectificación, la corrección solicitada y documentos que la sustenten.',
            'Cualquier elemento que facilite localizar los datos.',
          ],
        },
        {
          type: 'p',
          text: 'Comunicaremos la determinación dentro de los 20 días siguientes a la recepción de una solicitud completa y, si resulta procedente, la haremos efectiva dentro de los 15 días siguientes. Estos plazos podrán ampliarse una sola vez por un periodo igual cuando exista causa justificada, conforme a la Ley.',
        },
        {
          type: 'p',
          text: 'La revocación o cancelación puede no ser inmediata cuando exista una obligación legal o contractual de conservar información. En tal caso, los datos serán bloqueados y posteriormente suprimidos conforme a los plazos aplicables.',
        },
      ],
    },
    {
      n: '9',
      title: 'Conservación y seguridad',
      blocks: [
        {
          type: 'p',
          text: 'Conservaremos los datos únicamente durante el tiempo necesario para las finalidades informadas y los plazos de prescripción, defensa, archivo y conservación previstos en las disposiciones aplicables. Después se bloquearán y eliminarán o anonimizarán de forma segura, cuando corresponda.',
        },
        {
          type: 'p',
          text: 'Aplicamos medidas administrativas, técnicas y físicas razonables según el riesgo, naturaleza y sensibilidad de la información. Ningún sistema es infalible; si ocurre una vulneración de seguridad que afecte de forma significativa sus derechos, se lo informaremos sin demora indebida conforme a la legislación aplicable.',
        },
      ],
    },
    {
      n: '10',
      title: 'Cookies y tecnologías de rastreo',
      blocks: [
        {
          type: 'p',
          text: 'El sitio puede usar cookies y tecnologías similares necesarias para su funcionamiento, seguridad y preferencias, así como herramientas de medición o publicidad cuando estén habilitadas. Estas tecnologías pueden obtener dirección IP, identificadores, navegador, sistema operativo, páginas visitadas, duración de la sesión, interacciones y sitio de procedencia.',
        },
        {
          type: 'p',
          text: 'Puede administrar o deshabilitar cookies desde la configuración de su navegador y, cuando exista, desde el panel de preferencias del sitio. La desactivación de cookies necesarias puede afectar algunas funciones. Confidenza deberá mantener actualizado en el sitio el detalle de proveedores, finalidades y duración de cookies no esenciales.',
        },
      ],
    },
    {
      n: '11',
      title: 'Datos de niñas, niños y adolescentes',
      blocks: [
        {
          type: 'p',
          text: 'Cuando sea necesario tratar datos de menores —por ejemplo, como asegurados o beneficiarios— Confidenza lo hará mediante quien ejerza patria potestad, tutela o representación legal, y aplicará medidas reforzadas de minimización y protección.',
        },
      ],
    },
    {
      n: '12',
      title: 'Cambios al aviso y autoridad competente',
      blocks: [
        {
          type: 'p',
          text: 'Podremos modificar este aviso por cambios legales, operativos o tecnológicos. La versión vigente y la fecha de actualización estarán disponibles en www.confidenzaseguros.com, en la sección “Aviso de Privacidad”. Cuando el cambio requiera un nuevo consentimiento, éste se recabará por el medio correspondiente.',
        },
        {
          type: 'p',
          text: 'Si considera vulnerado su derecho a la protección de datos personales, puede acudir ante la Secretaría Anticorrupción y Buen Gobierno, autoridad competente conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares.',
        },
        {
          type: 'p',
          text: 'Marco de referencia: Ley Federal de Protección de Datos Personales en Posesión de los Particulares vigente; su Reglamento; y Lineamientos del Aviso de Privacidad, en lo que continúen siendo aplicables.',
        },
      ],
    },
  ],
};

const en: PrivacyDoc = {
  title: 'Comprehensive Privacy Notice',
  version: 'Confidenza Seguros & Fianzas  |  Current version: August 11, 2026',
  intro:
    'This notice explains what personal data we process, what we use it for, with whom we share it, and how you can exercise your rights.',
  sections: [
    {
      n: '1',
      title: 'Identity and address of the data controller',
      blocks: [
        {
          type: 'p',
          text: 'Maria de Lourdes Camez Ramos and Rodolfo Olea Patiño, who jointly operate under the trade name Confidenza Seguros & Fianzas (hereinafter, “Confidenza”), are responsible for the processing of your personal data. Their shared address is Avenida Lázaro Cárdenas 2501, local POS 10-A, interior Plaza Pioneros, colonia El Médano, Cabo San Lucas, Baja California Sur, C.P. 23453, Mexico.',
        },
        {
          type: 'p',
          text: 'Each agent shall be responsible for the files, policies, bonds, and operations brokered under their respective agent code. Both shall be responsible for the processing carried out through the office’s shared emails, files, databases, systems, technology providers, and administrative processes. Requests related to either of the two controllers will be handled through the same privacy channel.',
        },
        {
          type: 'p',
          text: 'Privacy contact: administracion@confidenzaseguros.com | Tel. 624 131 6666 | www.confidenzaseguros.com',
        },
      ],
    },
    {
      n: '2',
      title: 'Individuals to whom this notice applies',
      blocks: [
        {
          type: 'p',
          text: 'This applies to applicants, prospects, policyholders, insured parties, bonded parties, beneficiaries, providers of funds, joint obligors, principals, affected third parties, claimants, website visitors, business contacts, suppliers, job applicants, employees, and any person whose data is processed by Confidenza.',
        },
      ],
    },
    {
      n: '3',
      title: 'Personal data we may process',
      blocks: [
        {
          type: 'p',
          text: 'Depending on the relationship and the product or service requested, we may process the following categories:',
        },
        {
          type: 'ul',
          items: [
            'Identification and contact: name, business name, date of birth, age, sex, nationality, marital status, address, phone numbers, email, signature, photograph, and official identifications.',
            'Tax and billing: RFC (tax ID), proof of tax status, tax regime, and data required to issue invoices.',
            'Employment and academic: occupation, position, employer, seniority, income, experience, references, and education.',
            'Patrimonial and financial: income, assets, bank accounts, CLABE, payment data, financial history and capacity, and any other information needed to quote, contract, or administer insurance and bonds. Confidenza must not retain card security codes.',
            'Legal, administrative, insurance, and bonds: contractual information, policies, claims, losses, guarantees, files, administrative or judicial proceedings, traffic and immigration, where applicable.',
            'Third-party data: beneficiaries, dependents, family members, references, drivers, owners, principals, joint obligors, or affected third parties. Whoever provides such data must have the authority or authorization to do so and must inform them of this notice.',
            'Technical and browsing data: IP address, device identifiers, browser, operating system, pages viewed, date and time, source of the visit, cookies, and similar technologies.',
          ],
        },
        {
          type: 'p',
          lead: 'Sensitive personal data.',
          text: 'When indispensable to assess risks, quote, contract, or manage claims, we may process health data, medical history, disability, physical characteristics, biometric data, or other relevant sensitive data. We will not request information about union membership or sexual life unless strictly necessary and legally appropriate for the specific case.',
        },
        {
          type: 'p',
          text: 'For patrimonial or financial data and, especially, for sensitive data, we will obtain express consent or express written consent when the law requires it, except for the applicable legal exceptions.',
        },
      ],
    },
    {
      n: '4',
      title: 'Primary and necessary purposes',
      blocks: [
        { type: 'p', text: 'We will process your data for the following essential purposes:' },
        {
          type: 'ul',
          items: [
            'Identify you, contact you, compile and update your file, and verify the information provided.',
            'Understand your needs, assess risks, request and compare quotes, prepare proposals, and manage insurance or bond requests.',
            'Broker, contract, issue, administer, maintain, modify, collect, finance, renew, or cancel policies, bonds, endorsements, and related services.',
            'Manage claims, losses, assistance, indemnifications, salvage, damage recovery, and dealings with insurers, bonding companies, and service providers.',
            'Perform identity, solvency, fraud-prevention, illicit-operations-prevention, and regulatory-compliance validations.',
            'Comply with contractual, tax, accounting, insurance, and bonding obligations, and respond to requests from competent authorities.',
            'Handle requests, complaints, clarifications, inquiries, ARCO rights, revocations, and limitations of use or disclosure.',
            'Protect the safety of people, information, systems, and facilities; generate backups, logs, and evidence of operations.',
            'Select, hire, and manage personnel; verify references; comply with labor and social-security obligations, when the person is a job applicant or employee.',
            'Manage relationships with suppliers and service providers, including payments, access controls, and contractual compliance.',
            'Produce statistics, service models, and internal analyses using, where feasible, aggregated or dissociated data.',
          ],
        },
      ],
    },
    {
      n: '5',
      title: 'Secondary purposes',
      blocks: [
        {
          type: 'p',
          text: 'Where applicable, we may use contact data and commercial profile to send information about Confidenza’s products, renewals, promotions, surveys, events, and content, as well as to measure service quality. These purposes are not necessary to maintain the legal relationship.',
        },
        {
          type: 'p',
          text: 'You may refuse or withdraw your authorization for these purposes at any time by writing to administracion@confidenzaseguros.com with the subject “Marketing opt-out.” Refusal will not affect the products or services you have contracted.',
        },
      ],
    },
    {
      n: '6',
      title: 'Technology providers, automation, and artificial intelligence',
      blocks: [
        {
          type: 'p',
          text: 'Confidenza may rely on data processors that provide hosting, cloud, email, communications, cybersecurity, backup, document-management, CRM, analytics, support, and AI-assisted automation services, including platforms such as Handle, when enabled by Confidenza.',
        },
        {
          type: 'p',
          text: 'These providers will process data solely on behalf of and under the instructions of Confidenza, for the purposes described in this notice and in accordance with contracts and confidentiality and security measures. They may operate infrastructure inside or outside Mexico. Confidenza will seek to apply access, minimization, and configuration controls that prevent incompatible uses.',
        },
        {
          type: 'p',
          text: 'Main categories of providers that may process data on behalf of Confidenza:',
        },
        {
          type: 'table',
          headers: ['Provider or category', 'Service performed on behalf of Confidenza'],
          rows: [
            ['Handle', 'Automation and artificial intelligence applied to insurance operations, analysis, and support in administrative tasks.'],
            ['Quattro CRM / Copsis', 'Management of clients, policies, renewals, collections, documentation, and claims follow-up.'],
            ['Email, cloud, and storage', 'Receipt of communications, hosting, backup, exchange, and retention of files and records.'],
            ['Communications, support, and cybersecurity', 'Service via digital channels, maintenance, monitoring, access control, and systems protection.'],
            ['Other CRMs or equivalent platforms', 'Replace or complement the above functions under equivalent privacy and security obligations.'],
          ],
        },
        {
          type: 'p',
          text: 'The identified providers are illustrative and may be replaced or complemented by others performing equivalent functions, provided they act on behalf of and under the instructions of Confidenza and assume equivalent contractual obligations of privacy, confidentiality, and security.',
        },
        {
          type: 'p',
          text: 'Unless expressly informed and authorized otherwise, Confidenza will not allow providers to use personal data or client records to train their own or third parties’ general-purpose models. Confidenza’s personnel will review the relevant information before making decisions that produce legal effects or significantly affect a person; AI will be used as support and will not replace human judgment or the decision that corresponds to insurers, bonding companies, or authorities.',
        },
        {
          type: 'p',
          text: 'You may request information about AI-assisted processing, request human review, or raise an objection through the privacy channel indicated in this notice. The feasibility of the request will depend on the applicable contractual and legal obligations.',
        },
      ],
    },
    {
      n: '7',
      title: 'Remittances and transfers',
      blocks: [
        { type: 'p', lead: '7.1 Data processors (remittances).', text: 'Confidenza may remit data to providers acting on its behalf —including technology and AI services— without this constituting a transfer, provided they process the information in accordance with Confidenza’s instructions and contractual obligations.' },
        { type: 'p', lead: '7.2 Transfers that may be carried out.', text: 'We may transfer the necessary data to the following recipients:' },
        {
          type: 'table',
          headers: ['Recipient or category', 'Purpose'],
          rows: [
            ['Insurers, bonding companies, reinsurers, and re-bonding companies', 'Quote, select risks, issue and administer contracts, handle claims, and prevent fraud.'],
            ['Agents, adjusters, experts, hospitals, doctors, workshops, assistance services, lawyers, and other providers linked to the service', 'Provide assistance, assess damages or health, and process losses, claims, and defenses.'],
            ['Administrative, tax, financial, judicial, and social-security authorities', 'Comply with legal obligations and respond to well-founded requests.'],
            ['Institutions and organizations in the insurance and bonding sector', 'Risk selection, exchange permitted by law, fraud prevention, and compliance.'],
            ['Banks, payment processors, and financial institutions', 'Process collections, refunds, payments, and indemnifications.'],
            ['Companies related to Confidenza', 'Internal administration and operational continuity; for marketing, only where a legal basis or applicable consent exists.'],
          ],
        },
        {
          type: 'p',
          text: 'When a transfer requires consent, Confidenza will request it through the corresponding mechanism. Consent will not be required in the cases provided by applicable law, among others, when the transfer is necessary to maintain or fulfill a legal relationship, comply with a law, or respond to a competent authority.',
        },
      ],
    },
    {
      n: '8',
      title: 'ARCO rights and other controls',
      blocks: [
        {
          type: 'p',
          text: 'You may request access to your data; its rectification; cancellation where appropriate; or object to specific processing. You may also revoke your consent and limit the use or disclosure of your data.',
        },
        {
          type: 'p',
          text: 'Send your request to administracion@confidenzaseguros.com or deliver it to the address indicated. It must include:',
        },
        {
          type: 'ol',
          items: [
            'Name and means for communicating the response.',
            'Document proving your identity and, where applicable, legal representation.',
            'Clear description of the data and of the right you wish to exercise.',
            'For rectification, the requested correction and supporting documents.',
            'Any element that helps locate the data.',
          ],
        },
        {
          type: 'p',
          text: 'We will communicate the determination within 20 days of receiving a complete request and, if applicable, will make it effective within the following 15 days. These periods may be extended once for an equal period when there is justified cause, in accordance with the Law.',
        },
        {
          type: 'p',
          text: 'Revocation or cancellation may not be immediate when there is a legal or contractual obligation to retain information. In such a case, the data will be blocked and subsequently deleted in accordance with the applicable periods.',
        },
      ],
    },
    {
      n: '9',
      title: 'Retention and security',
      blocks: [
        {
          type: 'p',
          text: 'We will retain the data only for the time necessary for the informed purposes and the periods of limitation, defense, archiving, and retention provided in the applicable provisions. Afterward, they will be blocked and securely deleted or anonymized, where appropriate.',
        },
        {
          type: 'p',
          text: 'We apply reasonable administrative, technical, and physical measures according to the risk, nature, and sensitivity of the information. No system is infallible; if a security breach occurs that significantly affects your rights, we will inform you without undue delay in accordance with applicable law.',
        },
      ],
    },
    {
      n: '10',
      title: 'Cookies and tracking technologies',
      blocks: [
        {
          type: 'p',
          text: 'The site may use cookies and similar technologies necessary for its operation, security, and preferences, as well as measurement or advertising tools when enabled. These technologies may obtain IP address, identifiers, browser, operating system, pages visited, session duration, interactions, and site of origin.',
        },
        {
          type: 'p',
          text: 'You can manage or disable cookies from your browser settings and, where available, from the site’s preferences panel. Disabling necessary cookies may affect some functions. Confidenza must keep the detail of providers, purposes, and duration of non-essential cookies updated on the site.',
        },
      ],
    },
    {
      n: '11',
      title: 'Data of children and adolescents',
      blocks: [
        {
          type: 'p',
          text: 'When it is necessary to process data of minors —for example, as insured parties or beneficiaries— Confidenza will do so through whoever exercises parental authority, guardianship, or legal representation, and will apply reinforced minimization and protection measures.',
        },
      ],
    },
    {
      n: '12',
      title: 'Changes to the notice and competent authority',
      blocks: [
        {
          type: 'p',
          text: 'We may modify this notice due to legal, operational, or technological changes. The current version and the update date will be available at www.confidenzaseguros.com, in the “Privacy Notice” section. When the change requires new consent, it will be obtained through the corresponding means.',
        },
        {
          type: 'p',
          text: 'If you believe your right to the protection of personal data has been violated, you may turn to the Secretaría Anticorrupción y Buen Gobierno, the competent authority under the Federal Law on the Protection of Personal Data Held by Private Parties.',
        },
        {
          type: 'p',
          text: 'Reference framework: the Federal Law on the Protection of Personal Data Held by Private Parties in force; its Regulations; and the Privacy Notice Guidelines, insofar as they remain applicable.',
        },
      ],
    },
  ],
};

const renderBlock = (block: Block, i: number): React.ReactNode => {
  switch (block.type) {
    case 'p':
      return (
        <p key={i} className="text-gray-600 leading-relaxed mb-4">
          {block.lead && <span className="font-semibold text-gray-900">{block.lead} </span>}
          {block.text}
        </p>
      );
    case 'ul':
      return (
        <ul key={i} className="list-disc pl-6 space-y-2 mb-4 text-gray-600 leading-relaxed">
          {block.items.map((it, j) => (
            <li key={j}>{it}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={i} className="list-decimal pl-6 space-y-2 mb-4 text-gray-600 leading-relaxed">
          {block.items.map((it, j) => (
            <li key={j}>{it}</li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div key={i} className="overflow-x-auto mb-4">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead>
              <tr className="bg-primary-50">
                {block.headers.map((h, j) => (
                  <th key={j} className="border border-gray-200 px-4 py-2 text-left font-semibold text-gray-900">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, j) => (
                <tr key={j} className="align-top">
                  {row.map((cell, k) => (
                    <td key={k} className="border border-gray-200 px-4 py-2 text-gray-600">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

const PrivacyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const doc = i18n.language === 'en' ? en : es;

  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{doc.title}</h1>
          <p className="text-primary-100 text-sm md:text-base">{doc.version}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-gray-600 leading-relaxed mb-10">{doc.intro}</p>

          {doc.sections.map((section) => (
            <div key={section.n} className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                {section.n ? `${section.n}. ` : ''}
                {section.title}
              </h2>
              {section.blocks.map(renderBlock)}
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default PrivacyPage;
